import React from 'react';
import { toast } from 'react-toastify';
import { API } from 'aws-amplify';
import { t } from 'i18next';

import reducer from './reducer';

const initialState = {
    isDoingAjax: false,
    newPassword: '',
    isNewPasswordError: false,
    newPasswordErrorReason: '',
    isNewPasswordMasked: true,
    confirmNewPassword: '',
    isConfirmNewPasswordError: false,
    confirmNewPasswordErrorReason: '',
    isConfirmNewPasswordMasked: true,
};

/**A React Hook for controlling the state of the ChangePassword UI.
 * @param {string} userId The UserID parameter from the URL.
 * @param {string} callback A callback function to handle any cleanup or state changes once the Password changes.
 * @returns {{
 * StateVariables: any, isDoingAjax: boolean, updateState: function, handleSubmit: function,
 * }} An object containing the state variables and functions necessary for the UI. Examples of StateVariables include newPassword, isNewPasswordError, confirmNewPasswordErrorReason, etc.
 */
const useChangePassword = (userId, callback) => {
    const [state, dispatch] = React.useReducer(reducer, initialState);

    /**Returns reason this password does not meet complexity requirements.
     * @param {string} pwd Password to examine.
     * @returns {string|false} Failure reason, if there is one.
     */
    const complexityFailureReason = (pwd) => {
        if (!/\d/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordNumber');
        if (!/[~!@#$%^&]/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordSymbol');
        if (!/.{8}/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordLength');
        if (!/[A-Z]/.test(pwd)) return t('Authentication.CreateAccount.EnterUppercase');
        if (!/[a-z]/.test(pwd)) return t('Authentication.CreateAccount.EnterLowercase');

        return false;
    };

    const updateState = (key, value) => {
        let newState = {
            [key]: value,
        };
        switch (key) {
            case 'newPassword':
            case 'confirmNewPassword':
                let capKey = key[0].toUpperCase() + key.slice(1);
                newState[`is${capKey}Error`] = false;
                newState[`${key}ErrorReason`] = '';
                break;
            default:
                break;
        }

        dispatch({
            type: 'update',
            newState,
        });
    };

    const handleSubmit = async () => {
        dispatch({ type: 'doingAjax' });
        let newPasswordInvalid = complexityFailureReason(state.newPassword);
        let confirmNewPasswordInvalid = complexityFailureReason(state.confirmNewPassword);

        if (typeof newPasswordInvalid === 'string' || typeof confirmNewPasswordInvalid === 'string') {
            dispatch({
                type: 'update',
                newState: {
                    isNewPasswordError: newPasswordInvalid !== false,
                    newPasswordErrorReason: newPasswordInvalid || '',
                    isConfirmNewPasswordError: confirmNewPasswordInvalid !== false,
                    confirmNewPasswordErrorReason: confirmNewPasswordInvalid || '',
                    isDoingAjax: false,
                },
            });
            return;
        } else if (state.newPassword !== state.confirmNewPassword) {
            dispatch({
                type: 'update',
                newState: {
                    isNewPasswordError: true,
                    newPasswordErrorReason: 'Passwords must match.',
                    isConfirmNewPasswordError: true,
                    confirmNewPasswordErrorReason: 'Passwords must match.',
                    isDoingAjax: false,
                },
            });
            return;
        }

        try {
            await API.patch('ShipyardAPI', `/usersettings/${userId}/password`, {
                body: {
                    newPass: state.newPassword,
                },
            });
            toast.success(t('General.ChangesSaved'));
        } catch (postError) {
            console.error(postError);
            toast.error(t('General.AjaxError'));
        }
        dispatch({ type: 'ajaxDone' });
        callback?.();
    };

    return {
        ...state,
        updateState,
        handleSubmit,
    };
};

export default useChangePassword;
